import { Maybe, None, Some } from 'monet';
import notifierMixin from './notifierMixin';

/**
 * @description Ключи ошибок секции "Дополнительно".
 * @type {string[]}
 */
const OPTIONAL_SECTION_ERROR_FIELD_KEYS = [
  'birth-date',
  'cabinet-id',
  'inn',
  'snils',
];

/**
 * @description Ключи ошибок секции "ЕГИСЗ".
 * @type {string[]}
 */
const EGISZ_SECTION_ERROR_FIELD_KEYS = [
  'nsi-position',
];

/**
 * @description Примесь профиля сотрудника.
 */
export default {
  computed: {
    defaultConfidentialityLevelId() {
      const { confidentialityLevels } = this.employeeProfileOptions;
      return confidentialityLevels.slice(0, 1).map(({ value }) => value).at(0);
    },
    /**
     * @description Данные для редактирования профиля сотрудника.
     * @type {any}
     */
    employeeProfileOptions({ employeeProfileEditOptions, fetchEmployeeProfileOptions }) {
      return employeeProfileEditOptions.orLazy(fetchEmployeeProfileOptions);
    },
    /**
     * @description Реквизиты сотрудника.
     * @returns {any}
     */
    employeeProfileProps() {
      return this.employeeProfile.fold({
        initialConfidentialityLevelId: this.defaultConfidentialityLevelId,
      })((employee) => ({
        initialBirthDate: employee.birthDate,
        initialCabinetIds: employee.cabinetIds,
        initialConfidentialityLevelId: employee.confidentialityLevelId
          || this.defaultConfidentialityLevelId,
        initialDismissedDate: employee.dismissedDate,
        initialEmail: employee.email,
        initialHasTelegramByPhone: employee.hasTelegramByPhone,
        initialHasUser: employee.hasUser,
        initialHasWhatsAppByPhone: employee.hasWhatsAppByPhone,
        initialId: employee.id,
        initialInfo: employee.info,
        initialInn: employee.inn,
        initialIsAllowedCashbox: employee.isAllowedCashbox,
        initialIsAllowedLogin: employee.isAllowedLogin,
        initialIsDismissed: employee.isDismissed,
        initialIsEnabledWorkingScheduling: employee.isEnabledWorkingScheduling,
        initialIsForbidSelectOtherCabinetsInSchedule:
          employee.isForbidSelectOtherCabinetsInSchedule,
        initialName: employee.name,
        initialNsiPositionIds:
          employee.nsiPositions.map((nsiPosition) => nsiPosition.id.toString()),
        initialPatronymic: employee.patronymic,
        initialPhone: employee.phone.replace(/[ _)(-]/g, ''),
        initialRoleId: employee.roleId,
        initialSnils: employee.snils,
        initialSpecialtyIds: employee.specialities.map((speciality) => speciality.id),
        initialSpecialtyName: employee.specialities.map((speciality) => speciality.name)[0] ?? '',
        initialSurname: employee.surname,
        initialTimeStepMinutes: employee.timeStepMinutes,
        isOwner: employee.isOwner,
      }));
    },
    /**
     * @description Профиль загружен.
     * @returns {boolean}
     */
    hasEmployeeProfile: ({ employeeProfile }) => employeeProfile.isSome(),
  },
  data() {
    return {
      /**
       * @description Секция "дополнительно" развернута.
       * @type {boolean}
       */
      isOpenOptionalSection: false,
      /**
       * @description Секция "ЕГИС" развернута.
       * @type {boolean}
       */
      isOpenEgisSection: false,
      /**
       * @description Профиль сотрудника.
       * @type {Employee}
       */
      employeeProfile: None(),
      /**
       * @description Разрешения выбранного сотрудника
       * @type {Array}
       */
      selectedEmployeePermission: [],
      /**
       * @description Данные для редактирования профиля сотрудника.
       * @type {Maybe}
       */
      employeeProfileEditOptions: None(),
      /**
       * @description Ошибки валидации запроса на редактирование профиля сотрудника.
       * @type {any}
       */
      employeeProfileInputErrors: {},
    };
  },
  methods: {
    /**
     * @description Получит данные для редактирования профиля сотрудника.
     * @returns {any}
     */
    fetchEmployeeProfileOptions() {
      const onResponse = (result) => {
        const cabinetList = result
          .cabinetList
          .map(({ name: title, id: value }) => ({ title, value }));
        const confidentialityLevels = result
          .confidentialityLevels
          .map(({ name: title, id: value }) => ({ title, value }));
        const employeeRoleList = result
          .employeeRoleList
          .map(({ name: title, id: value, default_permissions: defaultPermissions }) => (
            { title, value, defaultPermissions }
          ));
        const employeeSpecialtyList = result
          .employeeSpecialtyList
          .map(({ name: title, id: value }) => ({ title, value }));
        const nsiPositionList = result
          .nsiPositionList
          .map(({ name: title, id: value }) => ({ title, value }));
        const { permissionsByGroups } = result;
        const timeStepList = result
          .timeStepList
          .map(({ title, number_minutes: value }) => ({ title, value }));
        this.employeeProfileEditOptions = Some({
          cabinetList,
          confidentialityLevels,
          employeeRoleList,
          employeeSpecialtyList,
          hasEgisz: result.hasEgisz,
          nsiPositionList,
          permissionsByGroups,
          timeStepList,
        });
      };
      this.$suggestionGateway.getEmployeeCreation().then(onResponse);
      return {
        cabinetList: [],
        confidentialityLevels: [],
        employeeRoleList: [],
        employeeSpecialtyList: [],
        hasEgisz: false,
        nsiPositionList: [],
        permissionsByGroups: [],
        timeStepList: [],
      };
    },
    /**
     * @description Получить профиль сотрудника.
     * @param {number} id Идентификатор сотрудника.
     * @returns {Promise}
     */
    fetchEmployeeProfile(id) {
      return new Promise((resolve, reject) => {
        const onFail = (error) => {
          this.notifyFail(error.title);
          reject();
        };
        const onSuccess = async (employee) => {
          this.employeeProfileInputErrors = {};
          await this.viewEmployeePermissions(id);
          this.employeeProfile = Some(employee);
          resolve();
        };
        const response = this.$employeeGateway.getById(id);
        response.then((result) => result.fold(onFail, onSuccess));
      });
    },
    /**
     * @description Принудительно открыть секцию при наличии ошибок.
     * @returns {void}
     */
    forceOpenOptionalSectionIfContainsErrors() {
      const optionalErrors = Object.keys(this.employeeProfileInputErrors).some(
        (candidate) => OPTIONAL_SECTION_ERROR_FIELD_KEYS.includes(candidate),
      );
      const egisErrors = Object.keys(this.employeeProfileInputErrors).some(
        (candidate) => EGISZ_SECTION_ERROR_FIELD_KEYS.includes(candidate),
      );
      this.isOpenOptionalSection = optionalErrors || this.isOpenOptionalSection;
      this.isOpenEgisSection = egisErrors || this.isOpenEgisSection;
    },
    /**
     * @description Показать права сотрудника.
     * @param {number} id Сотрудник.
     * @returns {Promise}
     */
    viewEmployeePermissions(id) {
      return new Promise((resolve) => {
        const onSuccess = (permissions) => {
          this.selectedEmployeePermission = permissions;
          resolve();
        };
        this.$employeeGateway
          .viewPermissionListByEmployeeId(id)
          .then((result) => result.fold(this.notifyFail, onSuccess));
      });
    },
    /**
     * @description Отправить запрос на редактирование профиля сотрудника.
     * @param {EmployeeEditRequest} request Запрос.
     * @returns {Promise}
     */
    sendEmployeeProfileEditRequest(request) {
      return new Promise((resolve, reject) => {
        const onFail = (error) => {
          this.employeeProfileInputErrors = error.allCauses();
          this.forceOpenOptionalSectionIfContainsErrors();
          if (!error.hasCauses()) {
            this.notifyFail(error.getTitle());
          }
          reject();
        };
        const onSuccess = (result) => {
          this.sendEmployeePermissionEditRequest(request.getPermissions()).then(() => {
            this.notifySuccess(result.title);
            this.employeeProfileInputErrors = {};
            resolve();
          });
        };
        const response = this.$employeeGateway.edit(request);
        response.then((result) => result.fold(onFail, onSuccess));
      });
    },
    /**
     * @description Отправить запрос на редактирование разрешений сотрудника
     * @return {Promise}
     */
    sendEmployeePermissionEditRequest(request) {
      return new Promise((resolve) => {
        const onSuccess = () => {
          resolve();
        };
        this
          .$employeeGateway
          .editPermissionListByEmployeeId(request)
          .then((result) => result.fold(this.notifyFail, onSuccess));
      });
    },
    /**
     * @description Сбросить профиль сотрудника.
     * @returns {void}
     */
    resetEmployeeProfile() {
      this.employeeProfile = Maybe.none();
    },
    /**
     * @description Обработать переключение видимости секции "Дополнительно".
     * @param {boolean} isOpen
     * @returns {void}
     */
    onCollapseOptionalSection(isOpen) {
      this.isOpenOptionalSection = isOpen;
    },
    /**
     * @description Обработать переключение видимости секции "ЕГИСЗ".
     * @param {boolean} isOpen
     * @returns {void}
     */
    onCollapseEgisSection(isOpen) {
      this.isOpenEgisSection = isOpen;
    },
    /**
     * @description Обработать событие изменения пароля.
     * @param {number} employeeId
     * @returns {void}
     */
    onChangePassword(employeeId) {
      const onFail = (error) => {
        this.profileInputErrors = error.allCauses();
        if (!error.hasCauses()) {
          this.notifyFail(error.getTitle());
        }
      };
      const onSuccess = (result) => {
        this.notifySuccess(result.title);
      };
      this
        .$employeeGateway
        .changePassword(employeeId)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
  mixins: [notifierMixin],
};
